import { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import type { DashboardType, UpdateWidgetPreference } from '@/types/dashboard';
import { useDashboard } from '@/hooks/use-dashboard';
import { GripVertical, Eye, EyeOff, RotateCcw } from 'lucide-react';
import { cn } from '@/lib/utils';

interface WidgetCustomizationModalProps {
  readonly open: boolean;
  readonly onOpenChange: (open: boolean) => void;
  readonly dashboardType: DashboardType;
}

export function WidgetCustomizationModal({
  open,
  onOpenChange,
  dashboardType,
}: WidgetCustomizationModalProps) {
  const { useDashboardPreferences, useUpdateWidgetPreferences, useResetToDefaults } = useDashboard();
  const { data: dashboardPrefs } = useDashboardPreferences(dashboardType);
  const updateWidgets = useUpdateWidgetPreferences();
  const resetToDefaults = useResetToDefaults();

  const [widgets, setWidgets] = useState<UpdateWidgetPreference[]>([]);

  useEffect(() => {
    if (dashboardPrefs?.widgets) {
      const sortedWidgets = [...dashboardPrefs.widgets].sort((a, b) => a.displayOrder - b.displayOrder);
      setWidgets(
        sortedWidgets.map((w) => ({
            widgetId: w.widgetId,
            isEnabled: w.isEnabled,
            displayOrder: w.displayOrder,
            customSettings: w.customSettings,
          }))
      );
    }
  }, [dashboardPrefs]);

  const handleToggleWidget = (widgetId: string) => {
    setWidgets((prev) =>
      prev.map((w) =>
        w.widgetId === widgetId ? { ...w, isEnabled: !w.isEnabled } : w
      )
    );
  };

  const handleSave = async () => {
    try {
      await updateWidgets.mutateAsync({
        dashboardType,
        widgets,
      });
      onOpenChange(false);
    } catch (error) {
      console.error('Failed to update widgets:', error);
    }
  };

  const handleResetToDefaults = async () => {
    if (confirm('Are you sure you want to reset this dashboard to default settings?')) {
      try {
        await resetToDefaults.mutateAsync(dashboardType);
        onOpenChange(false);
      } catch (error) {
        console.error('Failed to reset dashboard:', error);
      }
    }
  };

  const enabledCount = widgets.filter((w) => w.isEnabled).length;
  const totalCount = widgets.length;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Customise Dashboard</DialogTitle>
          <DialogDescription>
            Enable or disable widgets for your{' '}
            {dashboardType === 'financial' ? 'Financial' : 'Asset Management'}{' '}
            dashboard. Changes will be saved automatically.
          </DialogDescription>
        </DialogHeader>

        <div className="py-4">
          <div className="flex items-center justify-between mb-4">
            <p className="text-sm text-muted-foreground">
              {enabledCount} of {totalCount} widgets enabled
            </p>
          </div>

          <div className="space-y-2">
            {widgets.map((widget, index) => (
              <div
                key={widget.widgetId}
                className={cn(
                  'flex items-center gap-3 p-3 rounded-lg border transition-colors',
                  widget.isEnabled ? 'bg-accent/50' : 'bg-muted/30'
                )}
              >
                <GripVertical className="h-5 w-5 text-muted-foreground cursor-move" />
                
                <div className="flex-1 min-w-0">
                  <Label
                    htmlFor={`widget-${widget.widgetId}`}
                    className="text-sm font-medium cursor-pointer"
                  >
                    {getWidgetName(widget.widgetId)}
                  </Label>
                  <p className="text-xs text-muted-foreground mt-0.5">
                    {getWidgetDescription(widget.widgetId)}
                  </p>
                </div>

                <div className="flex items-center gap-2">
                  {widget.isEnabled ? (
                    <Eye className="h-4 w-4 text-muted-foreground" />
                  ) : (
                    <EyeOff className="h-4 w-4 text-muted-foreground" />
                  )}
                  <Switch
                    id={`widget-${widget.widgetId}`}
                    checked={widget.isEnabled}
                    onCheckedChange={() => handleToggleWidget(widget.widgetId)}
                  />
                </div>
              </div>
            ))}
          </div>
        </div>

        <DialogFooter className="flex-col sm:flex-row gap-2">
          <div className="flex-1">
            <Button
              variant="outline"
              onClick={handleResetToDefaults}
              disabled={resetToDefaults.isPending}
              className="w-full sm:w-auto"
            >
              <RotateCcw className={cn("h-4 w-4 mr-2", resetToDefaults.isPending && "animate-spin")} />
              Reset to Defaults
            </Button>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button onClick={handleSave} disabled={updateWidgets.isPending}>
              {updateWidgets.isPending ? 'Saving...' : 'Save Changes'}
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// Helper functions to get widget names and descriptions
function getWidgetName(widgetId: string): string {
  const names: Record<string, string> = {
    // Financial Dashboard
    'total-asset-value': 'Total Asset Value',
    'depreciation-overview': 'Depreciation Overview',
    'asset-value-trend': 'Asset Value Trend',

    'depreciation-by-category': 'Depreciation by Category',

    'top-assets-by-value': 'Top Assets by Value',
    'insurance-compliance': 'Insurance & Compliance',
    'warranty-status': 'Warranty Status',
    'financial-summary': 'Financial Summary',
    'budget-vs-actual': 'Budget vs Actual',
    
    // Asset Management Dashboard
    'asset-health-score': 'Asset Health Score',
    'maintenance-overview': 'Maintenance Overview',
    'asset-lifecycle': 'Asset Lifecycle',
    'maintenance-schedule': 'Maintenance Schedule',
    'asset-utilization': 'Asset Utilisation',
    'recent-activity': 'Recent Activity',
    'asset-alerts': 'Asset Alerts',
    'maintenance-costs': 'Maintenance Costs',
    'asset-performance': 'Asset Performance',
    'location-overview': 'Location Overview',
    
    // Shared widgets
    'assets-by-category': 'Assets by Category',
    'assets-by-location': 'Assets by Location',
    'assets-by-status': 'Assets by Status',
  };
  return names[widgetId] || widgetId;
}

function getWidgetDescription(widgetId: string): string {
  const descriptions: Record<string, string> = {
    // Financial Dashboard
    'total-asset-value': 'Current total value of all assets',
    'depreciation-overview': 'Depreciation summary and trends',
    'asset-value-trend': 'Historical asset value changes',

    'depreciation-by-category': 'Depreciation grouped by category',

    'top-assets-by-value': 'Highest value assets',
    'insurance-compliance': 'Insurance coverage and compliance',
    'warranty-status': 'Active warranties and expirations',
    'financial-summary': 'Key financial metrics overview',
    'budget-vs-actual': 'Budget comparison and variance',
    
    // Asset Management Dashboard
    'asset-health-score': 'Overall asset health metrics',
    'maintenance-overview': 'Maintenance status and schedule',
    'asset-lifecycle': 'Asset age and lifecycle stages',
    'maintenance-schedule': 'Upcoming maintenance tasks',
    'asset-utilization': 'Asset usage and efficiency',
    'recent-activity': 'Latest asset activities',
    'asset-alerts': 'Important alerts and notifications',
    'maintenance-costs': 'Maintenance expense tracking',
    'asset-performance': 'Performance metrics and KPIs',
    'location-overview': 'Assets grouped by location',
    
    // Shared widgets
    'assets-by-category': 'Asset distribution by category',
    'assets-by-location': 'Asset distribution by location',
    'assets-by-status': 'Asset distribution by status',
  };
  return descriptions[widgetId] || 'Widget description';
}
